import { EditableFieldProps } from './editable-field-json-props'

export type ThumbnailSizePreset = 'sm' | 'md' | 'lg'
export type ThumbnailFit = 'cover' | 'contain'

/** Runtime shape stored in entity JSONB for fileSet-typed schema properties. */
export interface FileSetValue {
  files?: any[]
  /** Server-generated PDF aggregate of all files. Transient — not user-editable. */
  aggregate?: any
  /** Incremented by the server on each file-set mutation. */
  version?: number
}

/**
 * Extracts the files array from a fileSet value.
 *
 * Tolerates a bare array: values written before the fileSet migration are
 * still out there, and a field that assumes the object shape reads `.map` off
 * `{files: […]}` — or off an array, depending which way it guessed wrong.
 * Every read of a media field's value goes through here.
 */
export function getFilesetFiles(value: FileSetValue | any[] | undefined): any[] {
  if (Array.isArray(value)) return value
  return value?.files ?? []
}

/**
 * Rebuilds a fileSet value around a new files array, preserving the
 * server-owned fields (`aggregate`, `version`) on the way through.
 *
 * A pre-migration array value has none of those to preserve and must NOT be
 * spread — `{...['a','b']}` yields `{0: 'a', 1: 'b'}`, which is neither shape.
 * Every write of a media field's value goes through here.
 */
export function withFilesetFiles(value: FileSetValue | any[] | undefined, files: any[]): FileSetValue {
  const isFileSetObject = !!value && !Array.isArray(value) && typeof value === 'object'
  return isFileSetObject ? { ...(value as FileSetValue), files } : { files }
}

/**
 * Shared contract for field components that collect one or more file/image
 * attachments and display them as chips or thumbnails within a form.
 */
export const MEDIA_ATTACHMENT_FIELD_DEFAULT_PROPS: Partial<MediaAttachmentFieldProps> = {
  thumbnailSize: 'md',
  thumbnailFit: 'contain',
  showFileName: false,
  showDownloadButton: true,
  showPrintButton: true,
}

/**
 * Note the value type: every media field stores a `fileSet` object, never a
 * bare array. Each field used to re-declare that with
 * `Omit<…, 'value' | 'initialValue' | 'onChange'>`, which left this base
 * disagreeing with all of them — `MEDIA_ATTACHMENT_FIELD_DEFAULT_PROPS` then
 * wasn't assignable to any field's own `Partial<Props>`. It lives here now.
 */
export interface MediaAttachmentFieldProps extends EditableFieldProps<FileSetValue> {
  entity?: any

  // ─── Count limits ─────────────────────────────────────────────────────────
  /** Inclusive lower bound. `isRequired: true` with no explicit min defaults to 1. */
  minFiles?: number
  /** Inclusive upper bound. The Add affordance disappears once value.length >= max. */
  maxFiles?: number

  // ─── Add-more messaging ───────────────────────────────────────────────────
  /** Label on the add chip/button shown when files are already present. */
  addMoreMessage?: string

  // ─── Thumbnail / chip display ─────────────────────────────────────────────
  thumbnailSize?: ThumbnailSizePreset | number
  thumbnailFit?: ThumbnailFit
  showFileName?: boolean

  /**
   * Maximum number of thumbnails/chips to render inline before collapsing into
   * a "show N more" overflow control. Prevents the field from overtaking the form.
   */
  maxVisibleItems?: number

  // ─── Lightbox actions ─────────────────────────────────────────────────────
  showDownloadButton?: boolean
  showPrintButton?: boolean
  printOptions?: any

  // ─── Image processing ─────────────────────────────────────────────────────
  /** Short-edge target (px) for the image resize step. Defaults to 2350. */
  maxImageDimension?: number
  /** WebP quality (0–1) for the image recompression step. Defaults to 0.75. */
  imageQuality?: number
}

// ─── Count bounds + validation ──────────────────────────────────────────────

export interface FileCountBounds {
  min: number
  max?: number
}

/**
 * Resolves the effective `[min, max]` file count. `isRequired: true` with no
 * explicit `minFiles` reads as a min of 1; a `maxFiles` below the resolved min
 * is contradictory and is dropped rather than making the field unsatisfiable.
 */
export function resolveFileCountBounds(
  props: Pick<MediaAttachmentFieldProps, 'minFiles' | 'maxFiles' | 'isRequired'>,
): FileCountBounds {
  const { minFiles, maxFiles, isRequired } = props
  const min = minFiles ?? (isRequired ? 1 : 0)
  const max = maxFiles != null && maxFiles < min ? undefined : maxFiles
  return { min, max }
}

/**
 * A field with a positive minimum is required by definition. `isFileCountRequired`
 * passes `true` to the shell so the asterisk shows; the error message for any
 * count violation (including count === 0) comes from `getFileCountError`.
 */
export function isFileCountRequired(
  props: Pick<MediaAttachmentFieldProps, 'minFiles' | 'maxFiles' | 'isRequired'>,
): boolean {
  return !!props.isRequired || resolveFileCountBounds(props).min > 0
}

/**
 * Message for a count that violates the bounds, or `undefined` when it is
 * within them. Only covers partial fills (0 < count < min) and over-max — the
 * count === 0 case is handled by `getCountErrors` on each field class so it
 * only fires at submit time, not on every render.
 */
export function getFileCountError(count: number, bounds: FileCountBounds): string | undefined {
  const { min, max } = bounds
  if (max != null && count > max) {
    return `no more than ${max} ${pluralizeFiles(max)} allowed`
  }
  if (count > 0 && count < min) {
    return `at least ${min} ${pluralizeFiles(min)} required`
  }
  return undefined
}

/**
 * Merges a count violation into the errors the renderer supplied.
 * NOTE: file-upload fields no longer call this at render time — all count
 * errors flow through `static getCountErrors` → `mergeUIStateErrors` so they
 * only appear after submit. This helper remains available for callers that
 * intentionally want immediate feedback (e.g. an over-max prefill shown on load).
 */
export function withFileCountError(
  errors: any[] | undefined,
  count: number,
  props: Pick<MediaAttachmentFieldProps, 'minFiles' | 'maxFiles' | 'isRequired' | 'isReadOnly' | 'isDisabled'>,
): any[] | undefined {
  if (props.isReadOnly || props.isDisabled) return errors
  const countError = getFileCountError(count, resolveFileCountBounds(props))
  if (!countError) return errors
  return [countError, ...(errors ?? [])]
}

function pluralizeFiles(count: number): string {
  return count === 1 ? 'file' : 'files'
}

// ─── File entry shape helpers ───────────────────────────────────────────────
// File entries reach these fields in several shapes: the native picker's image
// JSON (`{uniqueId, name, type, source, uri}`), the web ingest entry
// (`{name, type, size, uri, uniqueId}`), and bare uri strings.

/** True for entries the image viewers can actually render. */
export function isImageFileEntry(file: any): boolean {
  if (!file) return false
  if (file.type === 'image') return true
  if (file.type) return false
  const name = typeof file === 'string' ? file : file.name || file.uri
  return typeof name === 'string' && /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(name)
}

export function getFileEntryName(file: any, fallback = 'Untitled'): string {
  if (typeof file === 'string') return file
  return file?.name || fallback
}

export function getFileEntryUri(file: any): string | undefined {
  if (typeof file === 'string') return file
  return file?.source?.uri || file?.uri
}
